import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Unlock, AlertTriangle, CheckCircle, RefreshCw, Search, Filter, Eye, X, DollarSign, Calendar, FileText, MessageSquare, Ban, RotateCcw, Bell } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import {
  sendEscrowReleasedEmail,
  sendEscrowDisputedEmail,
  sendEscrowRefundedEmail,
  sendEscrowResolvedEmail,
} from '../../../services/escrowEmailService';
import EscrowDigitalSignatureModal from './EscrowDigitalSignatureModal';


// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  DRAFT:           { label: 'Brouillon',       color: 'bg-gray-100 text-gray-700 border-gray-200' },
  OFFER_SENT:      { label: 'Offre envoyée',   color: 'bg-blue-100 text-blue-700 border-blue-200' },
  OFFER_ACCEPTED:  { label: 'Offre acceptée',  color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  PAYMENT_PENDING: { label: 'Paiement en att.', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  FUNDS_HELD:      { label: 'Fonds bloqués',   color: 'bg-amber-100 text-amber-700 border-amber-200' },
  PENDING:         { label: 'En attente',      color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  pending:         { label: 'En attente',      color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  SHIPPED:         { label: 'Expédié',         color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  DELIVERED:       { label: 'Livré',           color: 'bg-teal-100 text-teal-700 border-teal-200' },
  COMPLETED:       { label: 'Complété',        color: 'bg-green-100 text-green-700 border-green-200' },
  RELEASED:        { label: 'Libéré',          color: 'bg-green-100 text-green-700 border-green-200' },
  DISPUTE:         { label: 'Litige',          color: 'bg-red-100 text-red-700 border-red-200' },
  DISPUTED:        { label: 'Litige',          color: 'bg-red-100 text-red-700 border-red-200' },
  REFUNDED:        { label: 'Remboursé',       color: 'bg-purple-100 text-purple-700 border-purple-200' },
  CANCELLED:       { label: 'Annulé',          color: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const formatAmount = (amount, currency = 'XOF') => {
  if (!amount) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || { label: status, color: 'bg-gray-100 text-gray-600 border-gray-200' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      {cfg?.label}
    </span>
  );
};

// ─── Confirm Action Modal ─────────────────────────────────────────────────────
const ConfirmModal = ({ action, transaction, onConfirm, onCancel, loading }) => {
  const [note, setNote] = useState('');
  const [disputeReason, setDisputeReason] = useState('');

  const isRelease = action === 'release';
  const isDispute = action === 'dispute';
  const isRefund   = action === 'refund';
  const isResolve  = action === 'resolve';

  const config = {
    release: { title: 'Libérer les fonds', icon: Unlock, iconBg: 'bg-green-100', iconColor: 'text-green-600', btnClass: 'bg-green-600 hover:bg-green-700', btnLabel: 'Confirmer la libération' },
    dispute: { title: 'Ouvrir un litige',  icon: AlertTriangle, iconBg: 'bg-red-100', iconColor: 'text-red-600', btnClass: 'bg-red-600 hover:bg-red-700', btnLabel: 'Ouvrir le litige' },
    refund:  { title: 'Rembourser l\'acheteur', icon: RotateCcw, iconBg: 'bg-purple-100', iconColor: 'text-purple-600', btnClass: 'bg-purple-600 hover:bg-purple-700', btnLabel: 'Confirmer le remboursement' },
    resolve: { title: 'Résoudre le litige', icon: CheckCircle, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', btnClass: 'bg-blue-600 hover:bg-blue-700', btnLabel: 'Marquer comme résolu' },
  };

  const cfg = config?.[action];
  const Icon = cfg?.icon;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${cfg?.iconBg} flex items-center justify-center`}>
                {Icon && <Icon className={`w-5 h-5 ${cfg?.iconColor}`} />}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{cfg?.title}</h3>
                <p className="text-sm text-gray-500">Réf: {transaction?.reference || transaction?.id?.slice(0, 8)}</p>
              </div>
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Transaction summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Montant total</span>
              <span className="font-semibold text-gray-900">{formatAmount(transaction?.amount_total || transaction?.total_amount, transaction?.currency)}</span>
            </div>
            {isRelease && (
              <div className="flex justify-between">
                <span className="text-gray-500">Paiement vendeur</span>
                <span className="font-semibold text-green-700">{formatAmount(transaction?.seller_payout_amount || transaction?.seller_amount, transaction?.currency)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Statut actuel</span>
              <StatusBadge status={transaction?.status} />
            </div>
          </div>

          {isDispute && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Motif du litige <span className="text-red-500">*</span></label>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e?.target?.value)}
                rows={3}
                placeholder="Décrivez le motif du litige..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Note administrative {isDispute ? '' : '(optionnel)'}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              rows={2}
              placeholder="Note interne pour l'audit..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => onConfirm({ note, disputeReason })}
              disabled={loading || (isDispute && !disputeReason?.trim())}
              className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cfg?.btnClass}`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Traitement...
                </span>
              ) : cfg?.btnLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Transaction Detail Drawer ────────────────────────────────────────────────
const TransactionDetail = ({ tx, disputes, onClose, onAction }) => {
  const txDisputes = disputes?.filter(d => d?.transaction_id === tx?.id) || [];

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end">
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Détail de la transaction</h3>
            <p className="text-sm text-gray-500">Réf: {tx?.reference || tx?.id?.slice(0, 8)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Status + amounts */}
          <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 font-medium">Statut</span>
              <StatusBadge status={tx?.status} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Montant total</p>
                <p className="text-lg font-bold text-gray-900">{formatAmount(tx?.amount_total || tx?.total_amount, tx?.currency)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Commission KPS</p>
                <p className="text-lg font-bold text-amber-600">{formatAmount(tx?.kps_commission_amount || tx?.platform_fee, tx?.currency)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Paiement vendeur</p>
                <p className="text-lg font-bold text-green-700">{formatAmount(tx?.seller_payout_amount || tx?.seller_amount, tx?.currency)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Taux commission</p>
                <p className="text-lg font-bold text-gray-700">{tx?.kps_commission_rate || '—'}%</p>
              </div>
            </div>
          </div>

          {/* Transaction info */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Informations</h4>
            <div className="space-y-2 text-sm">
              {tx?.title && (
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div><span className="text-gray-500">Titre: </span><span className="text-gray-900 font-medium">{tx?.title}</span></div>
                </div>
              )}
              {tx?.description && (
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div><span className="text-gray-500">Description: </span><span className="text-gray-900">{tx?.description}</span></div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div><span className="text-gray-500">Créé le: </span><span className="text-gray-900">{formatDate(tx?.created_at)}</span></div>
              </div>
              {tx?.escrow_release_date && (
                <div className="flex items-center gap-3">
                  <Unlock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div><span className="text-gray-500">Date libération: </span><span className="text-gray-900">{formatDate(tx?.escrow_release_date)}</span></div>
                </div>
              )}
              {tx?.payment_provider && (
                <div className="flex items-center gap-3">
                  <DollarSign className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div><span className="text-gray-500">Fournisseur: </span><span className="text-gray-900 font-medium">{tx?.payment_provider}</span></div>
                </div>
              )}
              {tx?.payment_intent_id && (
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0"><span className="text-gray-500">Intent ID: </span><span className="text-gray-900 font-mono text-xs break-all">{tx?.payment_intent_id}</span></div>
                </div>
              )}
              {tx?.error_message && (
                <div className="flex items-start gap-3 bg-red-50 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div><span className="text-red-600 font-medium">Erreur: </span><span className="text-red-700">{tx?.error_message}</span></div>
                </div>
              )}
            </div>
          </div>

          {/* Disputes */}
          {txDisputes?.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Litiges ({txDisputes?.length})
              </h4>
              {txDisputes?.map(d => (
                <div key={d?.id} className="border border-red-200 rounded-lg p-4 bg-red-50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-red-700 uppercase">{d?.status}</span>
                    <span className="text-xs text-gray-500">{formatDate(d?.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-800 font-medium">{d?.reason}</p>
                  {d?.resolution_note && (
                    <p className="text-xs text-gray-600 bg-white rounded p-2 border border-red-100">
                      <span className="font-semibold">Résolution: </span>{d?.resolution_note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-2">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Actions</h4>
            <div className="grid grid-cols-2 gap-3">
              {['FUNDS_HELD', 'PENDING', 'pending', 'DELIVERED', 'OFFER_ACCEPTED', 'PAYMENT_PENDING']?.includes(tx?.status) && (
                <button
                  onClick={() => onAction('release', tx)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                >
                  <Unlock className="w-4 h-4" />
                  Libérer fonds
                </button>
              )}
              {!['DISPUTE', 'DISPUTED', 'COMPLETED', 'RELEASED', 'REFUNDED', 'CANCELLED']?.includes(tx?.status) && (
                <button
                  onClick={() => onAction('dispute', tx)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Ouvrir litige
                </button>
              )}
              {['DISPUTE', 'DISPUTED']?.includes(tx?.status) && (
                <>
                  <button
                    onClick={() => onAction('resolve', tx)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Résoudre litige
                  </button>
                  <button
                    onClick={() => onAction('refund', tx)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Rembourser
                  </button>
                </>
              )}
              {!['COMPLETED', 'RELEASED', 'REFUNDED', 'CANCELLED']?.includes(tx?.status) && (
                <button
                  onClick={() => onAction('cancel', tx)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                >
                  <Ban className="w-4 h-4" />
                  Annuler
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────
const EscrowManagementPanel = () => {
  const [transactions, setTransactions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedTx, setSelectedTx] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { action, tx }
  const [signatureModalTx, setSignatureModalTx] = useState(null);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ total: 0, held: 0, disputed: 0, completed: 0, totalAmount: 0 });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [txRes, dispRes] = await Promise.all([
        supabase?.from('escrow_transactions')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('escrow_disputes')?.select('*')?.order('created_at', { ascending: false }),
      ]);
      const txData = txRes?.data || [];
      const dispData = dispRes?.data || [];
      setTransactions(txData);
      setDisputes(dispData);

      // Compute stats
      const held = txData?.filter(t => ['FUNDS_HELD', 'PENDING', 'pending']?.includes(t?.status))?.length;
      const disputed = txData?.filter(t => ['DISPUTE', 'DISPUTED']?.includes(t?.status))?.length;
      const completed = txData?.filter(t => ['COMPLETED', 'RELEASED']?.includes(t?.status))?.length;
      const totalAmount = txData?.reduce((s, t) => s + parseFloat(t?.amount_total || t?.total_amount || 0), 0);
      setStats({ total: txData?.length, held, disputed, completed, totalAmount });
    } catch (err) {
      console.error('Error fetching escrow data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter transactions
  const filtered = transactions?.filter(tx => {
    const matchStatus = statusFilter === 'ALL' || tx?.status === statusFilter;
    const q = search?.toLowerCase();
    const matchSearch = !q ||
      tx?.reference?.toLowerCase()?.includes(q) ||
      tx?.title?.toLowerCase()?.includes(q) ||
      tx?.id?.toLowerCase()?.includes(q) ||
      tx?.payment_intent_id?.toLowerCase()?.includes(q);
    return matchStatus && matchSearch;
  });

  // ─── Action handler ─────────────────────────────────────────────────────────
  const handleAction = async (action, tx) => {
    setConfirmModal({ action, tx });
  };

  const handleConfirm = async ({ note, disputeReason }) => {
    const { action, tx } = confirmModal;
    setActionLoading(true);
    try {
      let newStatus;
      if (action === 'release') newStatus = 'RELEASED';
      else if (action === 'dispute') newStatus = 'DISPUTED';
      else if (action === 'refund') newStatus = 'REFUNDED';
      else if (action === 'resolve') newStatus = 'COMPLETED';
      else if (action === 'cancel') newStatus = 'CANCELLED';

      // Get current admin user
      const { data: { user: adminUser } } = await supabase?.auth?.getUser();

      // Build update payload
      const updatePayload = {
        status: newStatus,
        updated_at: new Date()?.toISOString(),
        ...(action === 'release' ? {
          escrow_release_date: new Date()?.toISOString(),
          released_at: new Date()?.toISOString(),
          released_by_admin: adminUser?.id || null,
        } : {}),
      };

      // Update transaction status
      const { error } = await supabase
        ?.from('escrow_transactions')
        ?.update(updatePayload)
        ?.eq('id', tx?.id);

      if (error) throw error;

      // Create dispute record if opening a dispute
      if (action === 'dispute') {
        await supabase?.from('escrow_disputes')?.insert({
          transaction_id: tx?.id,
          raised_by: adminUser?.id,
          reason: disputeReason,
          status: 'open',
          resolution_note: note || null,
        });
      }

      // If resolving, update dispute record
      if (action === 'resolve') {
        await supabase
          ?.from('escrow_disputes')
          ?.update({ status: 'resolved', resolution_note: note || null, updated_at: new Date()?.toISOString() })
          ?.eq('transaction_id', tx?.id)
          ?.eq('status', 'open');
      }

      // ─── Log action to escrow_action_logs ──────────────────────────────────
      await supabase?.from('escrow_action_logs')?.insert({
        escrow_transaction_id: tx?.id,
        admin_user_id: adminUser?.id || null,
        admin_email: adminUser?.email || null,
        action,
        previous_status: tx?.status,
        new_status: newStatus,
        amount_affected: tx?.amount_total || tx?.total_amount || null,
        currency: tx?.currency || 'XOF',
        notes: note || disputeReason || null,
      })?.catch(err => console.warn('Action log insert failed:', err?.message));

      // ─── Send transactional email based on action ───────────────────────────
      const emailParams = {
        transactionRef: tx?.reference || tx?.id?.slice(0, 8),
        propertyTitle: tx?.title || tx?.item_title,
        amount: tx?.amount_total || tx?.total_amount,
        currency: tx?.currency || 'XOF',
      };

      // Fetch buyer info for email
      if (tx?.buyer_id) {
        const { data: buyerProfile } = await supabase
          ?.from('user_profiles')
          ?.select('full_name, email')
          ?.eq('id', tx?.buyer_id)
          ?.maybeSingle();

        if (buyerProfile?.email) {
          const baseParams = { ...emailParams, userId: tx?.buyer_id, userEmail: buyerProfile?.email, userName: buyerProfile?.full_name };
          if (action === 'release') {
            sendEscrowReleasedEmail({ ...baseParams, transactionId: tx?.id })?.catch(() => {});
          } else if (action === 'dispute') {
            sendEscrowDisputedEmail({ ...baseParams, disputeReason })?.catch(() => {});
          } else if (action === 'refund') {
            sendEscrowRefundedEmail(baseParams)?.catch(() => {});
          } else if (action === 'resolve') {
            sendEscrowResolvedEmail({ ...baseParams, resolutionNote: note })?.catch(() => {});
          }
        }
      }

      const labels = {
        release: 'Fonds libérés — Agent immobilier et acheteur notifiés',
        dispute: 'Litige ouvert',
        refund: 'Remboursement effectué',
        resolve: 'Litige résolu',
        cancel: 'Transaction annulée',
      };
      showToast(labels?.[action] + ' avec succès');
      setConfirmModal(null);
      setSelectedTx(null);
      await fetchData();
    } catch (err) {
      console.error('Action error:', err);
      showToast('Erreur: ' + (err?.message || 'Une erreur est survenue'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const statusOptions = ['ALL', 'FUNDS_HELD', 'PENDING', 'DISPUTE', 'DISPUTED', 'COMPLETED', 'RELEASED', 'REFUNDED', 'CANCELLED', 'DRAFT', 'OFFER_SENT', 'OFFER_ACCEPTED', 'PAYMENT_PENDING'];

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-white text-sm font-medium transition-all ${toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          {toast?.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast?.msg}
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total transactions', value: stats?.total, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Fonds bloqués', value: stats?.held, icon: Lock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Litiges actifs', value: stats?.disputed, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Complétées', value: stats?.completed, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
        ]?.map(s => (
          <div key={s?.label} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl ${s?.bg} flex items-center justify-center flex-shrink-0`}>
              <s.icon className={`w-6 h-6 ${s?.color}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s?.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              placeholder="Rechercher par référence, titre, ID..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e?.target?.value)}
              className="pl-9 pr-8 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white min-w-[180px]"
            >
              <option value="ALL">Tous les statuts</option>
              {statusOptions?.slice(1)?.map(s => (
                <option key={s} value={s}>{STATUS_CONFIG?.[s]?.label || s}</option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">
            Transactions en séquestre
            <span className="ml-2 text-sm font-normal text-gray-500">({filtered?.length} résultat{filtered?.length !== 1 ? 's' : ''})</span>
          </h3>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Chargement des transactions...</p>
          </div>
        ) : filtered?.length === 0 ? (
          <div className="p-12 text-center">
            <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucune transaction trouvée</p>
            <p className="text-gray-400 text-sm mt-1">Modifiez vos filtres ou attendez de nouvelles transactions</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Référence</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Titre / Description</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Montant</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Commission</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered?.map(tx => {
                  const hasDispute = disputes?.some(d => d?.transaction_id === tx?.id && d?.status === 'open');
                  const isReleased = tx?.status === 'RELEASED';
                  return (
                    <tr key={tx?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <span className="font-mono text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {tx?.reference || tx?.id?.slice(0, 8)}
                        </span>
                        {hasDispute && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-red-600 font-semibold">
                            <AlertTriangle className="w-3 h-3" />
                            Litige
                          </span>
                        )}
                        {isReleased && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-600 font-semibold">
                            <Bell className="w-3 h-3" />
                            Agent notifié
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 max-w-[200px]">
                        <p className="font-medium text-gray-900 truncate">{tx?.title || tx?.item_title || '—'}</p>
                        {tx?.description && <p className="text-xs text-gray-400 truncate mt-0.5">{tx?.description}</p>}
                      </td>
                      <td className="px-5 py-4 text-right font-semibold text-gray-900">
                        {formatAmount(tx?.amount_total || tx?.total_amount, tx?.currency)}
                      </td>
                      <td className="px-5 py-4 text-right text-amber-600 font-medium">
                        {formatAmount(tx?.kps_commission_amount || tx?.platform_fee, tx?.currency)}
                      </td>
                      <td className="px-5 py-4 text-center">
                        <StatusBadge status={tx?.status} />
                      </td>
                      <td className="px-5 py-4 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(tx?.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setSelectedTx(tx)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir détails"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {['FUNDS_HELD', 'PENDING', 'pending', 'DELIVERED', 'OFFER_ACCEPTED']?.includes(tx?.status) && (
                            <button
                              onClick={() => setSignatureModalTx(tx)}
                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Signatures numériques requises"
                            >
                              <Icon name="PenLine" size={16} />
                            </button>
                          )}
                          {['FUNDS_HELD', 'PENDING', 'pending', 'DELIVERED', 'OFFER_ACCEPTED']?.includes(tx?.status) && (
                            <button
                              onClick={() => handleAction('release', tx)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Libérer les fonds"
                            >
                              <Unlock className="w-4 h-4" />
                            </button>
                          )}
                          {['DISPUTE', 'DISPUTED']?.includes(tx?.status) && (
                            <button
                              onClick={() => handleAction('resolve', tx)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Résoudre le litige"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {!['DISPUTE', 'DISPUTED', 'COMPLETED', 'RELEASED', 'REFUNDED', 'CANCELLED']?.includes(tx?.status) && (
                            <button
                              onClick={() => handleAction('dispute', tx)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Ouvrir un litige"
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selectedTx && (
        <TransactionDetail
          tx={selectedTx}
          disputes={disputes}
          onClose={() => setSelectedTx(null)}
          onAction={(action, tx) => { setSelectedTx(null); handleAction(action, tx); }}
        />
      )}

      {/* Digital Signature Modal */}
      {signatureModalTx && (
        <EscrowDigitalSignatureModal
          transaction={signatureModalTx}
          onClose={() => setSignatureModalTx(null)}
          onTransferApproved={() => {
            setSignatureModalTx(null);
            showToast('Transfert approuvé — toutes les signatures obtenues');
            fetchData();
          }}
          isAdmin={true}
        />
      )}

      {/* Confirm modal */}
      {confirmModal && (
        <ConfirmModal
          action={confirmModal?.action}
          transaction={confirmModal?.tx}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmModal(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default EscrowManagementPanel;
